/**
 * Duelo de Equipos — pantalla del JUGADOR (vendedor).
 * Abre desde el enlace compartido: /juego/duelo-equipos/:code
 * Elige quién eres (una vez), luego juegas en tu turno. Todo en vivo por WS.
 */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Clock, Trophy, Volume2, VolumeX, Zap, Home } from "lucide-react";

import { useLang } from "@/context/LangContext";
import { CanalesLogo } from "@/components/CanalesLogo";
import {
  wsUrl, TEAM_COLOR, PlayerCard, Options, useCountdown, DuelShell,
} from "@/components/duel/DuelBits";
import {
  playReveal, playMiss, playApplause, playCountdown,
  isEnabled as soundEnabled, toggleEnabled as toggleSound,
} from "@/utils/sounds";

export default function TeamDuelPlayer() {
  const { code } = useParams();
  const { lang } = useLang();
  const wsRef = useRef(null);
  const lastTick = useRef(0);

  const [state, setState] = useState(null);
  const [conn, setConn] = useState("connecting");
  const [myId, setMyId] = useState(() => localStorage.getItem(`duel_player_${code}`) || "");
  const [snd, setSnd] = useState(soundEnabled());
  const [reveal, setReveal] = useState(null);

  useEffect(() => {
    let ws, alive = true;
    try { ws = new WebSocket(wsUrl(code)); } catch { setConn("error"); return; }
    wsRef.current = ws;
    ws.onopen = () => {
      if (!alive) return;
      setConn("open");
      const sid = localStorage.getItem(`duel_player_${code}`) || "";
      ws.send(JSON.stringify(sid ? { type: "join", role: "player", seller_id: sid } : { type: "join", role: "spectator" }));
    };
    ws.onclose = () => alive && setConn("closed");
    ws.onerror = () => alive && setConn("error");
    ws.onmessage = (ev) => {
      let msg; try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "error") { toast.error(msg.message || "Error"); return; }
      if (msg.status) setState(msg);
      switch (msg.type) {
        case "question": case "steal_open": case "sd_question": setReveal(null); break;
        case "correct": case "sd_result": case "steal_result":
          if (msg.type === "steal_result" && !msg.success) { playMiss(); setReveal({ correct: msg.correct, selected: msg.selected }); }
          else if (msg.type === "sd_result" && !msg.success) { playMiss(); setReveal({ correct: msg.correct, selected: msg.selected }); }
          else { playReveal(); setReveal({ correct: msg.correct, selected: msg.selected }); }
          break;
        case "wrong": playMiss(); setReveal({ correct: msg.correct, selected: msg.selected }); break;
        case "game_end": playApplause(); setReveal(null); break;
        default: break;
      }
    };
    return () => { alive = false; try { ws && ws.close(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const secs = useCountdown(state);
  useEffect(() => {
    if (state && ["playing", "steal", "sudden_death"].includes(state.status) && secs > 0 && secs <= 5 && secs !== lastTick.current) {
      lastTick.current = secs; playCountdown();
    }
  }, [secs, state]);

  const send = (m) => { const ws = wsRef.current; if (ws && ws.readyState === 1) ws.send(JSON.stringify(m)); };

  const pickMe = (sid) => {
    setMyId(sid);
    localStorage.setItem(`duel_player_${code}`, sid);
    send({ type: "join", role: "player", seller_id: sid });
  };

  const answer = (optId) => send({ type: "answer", option_id: optId });

  const me = state && myId ? [...(state.teams?.A?.players || []), ...(state.teams?.B?.players || [])].find((p) => p.seller_id === myId) : null;
  const myTurn = !!(state && myId && state.answerer === myId && ["playing", "steal", "sudden_death"].includes(state.status) && !reveal);

  const Header = () => (
    <div className="flex items-center justify-between px-4 pt-3 mb-3">
      <Link to="/juego/canales-dijeron" className="brutal-btn bg-white px-3 py-2 text-sm inline-flex items-center gap-1" data-testid="duel-back">
        <Home className="w-4 h-4" /> {lang === "es" ? "Salir" : "Exit"}
      </Link>
      <CanalesLogo width={104} />
      <button
        onClick={() => setSnd(toggleSound())}
        data-testid="duel-sound-toggle"
        className={`brutal-btn px-3 py-2 text-sm ${snd ? "bg-canales-yellow text-black" : "bg-white text-neutral-500"}`}
      >
        {snd ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
    </div>
  );

  if (conn === "connecting" || !state) {
    return <DuelShell><Header /><div className="text-center text-white/70 py-20"><Loader2 className="w-8 h-8 animate-spin inline" /><div className="mt-2 text-sm">{lang === "es" ? "Conectando…" : "Connecting…"}</div></div></DuelShell>;
  }
  if (conn === "error" || conn === "closed") {
    return <DuelShell><Header /><div className="brutal bg-white p-8 text-center max-w-md mx-auto mt-10"><div className="font-display font-black text-2xl mb-2">{lang === "es" ? "Sala no disponible" : "Room unavailable"}</div><p className="text-sm text-neutral-600">{lang === "es" ? "El duelo no existe o ya terminó." : "Duel not found or already finished."}</p></div></DuelShell>;
  }

  // Identity picker
  if (!myId || !me) {
    return (
      <DuelShell>
        <Header />
        <div className="max-w-md mx-auto px-4">
          <div className="brutal bg-white p-5" data-testid="duel-pick-me">
            <div className="font-display font-black text-2xl mb-1">{lang === "es" ? "¿Quién eres?" : "Who are you?"}</div>
            <p className="text-sm text-neutral-600 mb-3">{lang === "es" ? "Toca tu nombre para unirte a tu equipo." : "Tap your name to join your team."}</p>
            {["A", "B"].map((t) => (
              <div key={t} className="mb-3">
                <div className="text-[10px] font-display font-black uppercase tracking-widest mb-1" style={{ color: TEAM_COLOR[t] }}>
                  {state.teams?.[t]?.name || `Equipo ${t}`}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {(state.teams?.[t]?.players || []).map((p) => (
                    <button key={p.seller_id} onClick={() => pickMe(p.seller_id)} data-testid={`duel-pick-${p.seller_id}`}
                      className="brutal-btn text-white px-3 py-3 font-display font-black text-left" style={{ background: TEAM_COLOR[t] }}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DuelShell>
    );
  }

  const status = state.status;
  const winnerName = state.winner_team ? state.team_names?.[state.winner_team] : null;

  return (
    <DuelShell>
      <Header />
      <div className="max-w-lg mx-auto px-4 pb-10 space-y-3">
        {/* my identity */}
        <div className="brutal-sm px-3 py-2 inline-flex items-center gap-2 text-white" style={{ background: TEAM_COLOR[me.team] }} data-testid="duel-my-chip">
          <span className="font-display font-black">{me.name}</span>
          <span className="text-[10px] uppercase tracking-widest opacity-90">· {state.team_names?.[me.team]}</span>
        </div>

        {/* team scoreboards */}
        <div className="grid grid-cols-2 gap-2">
          {["A", "B"].map((t) => (
            <div key={t} className="brutal-sm p-2 text-white" style={{ background: TEAM_COLOR[t] }} data-testid={`duel-teamscore-${t}`}>
              <div className="text-[10px] font-display font-black uppercase tracking-widest truncate opacity-90">{state.team_names?.[t]}</div>
              <div className="font-display font-black text-3xl leading-none text-canales-yellow">{state.team_scores?.[t] ?? 0}</div>
            </div>
          ))}
        </div>

        {/* players */}
        <div className="grid grid-cols-2 gap-2">
          {["A", "B"].map((t) => (
            <div key={t} className="space-y-2">
              {(state.teams?.[t]?.players || []).map((p) => (
                <PlayerCard key={p.seller_id} player={p} isAnswerer={state.answerer === p.seller_id} maxStrikes={state.max_strikes} lang={lang} />
              ))}
            </div>
          ))}
        </div>

        {status === "lobby" && (
          <div className="brutal bg-white p-5 text-center" data-testid="duel-lobby">
            <div className="font-display font-black text-xl">{lang === "es" ? "Esperando al anfitrión…" : "Waiting for host…"}</div>
            <p className="text-sm text-neutral-600 mt-1">{lang === "es" ? "El duelo empezará pronto." : "The duel will start soon."}</p>
          </div>
        )}

        {status === "paused" && (
          <div className="brutal bg-canales-yellow p-4 text-center font-display font-black text-lg" data-testid="duel-paused">
            {lang === "es" ? "⏸ Juego en pausa" : "⏸ Game paused"}
          </div>
        )}

        {["playing", "steal", "sudden_death"].includes(status) && state.question && (
          <>
            <div className="flex items-center justify-between">
              <div className="brutal-sm bg-white px-2 py-1 text-xs font-display font-black">
                {status === "sudden_death"
                  ? (lang === "es" ? "MUERTE SÚBITA" : "SUDDEN DEATH")
                  : `${lang === "es" ? "Pregunta" : "Question"} ${Math.min(state.q_index + 1, state.q_total)}/${state.q_total}`}
              </div>
              <motion.div animate={secs <= 5 ? { scale: [1, 1.1, 1] } : {}} transition={{ repeat: Infinity, duration: 0.6 }}
                className="brutal-sm px-3 py-1 text-white font-display font-black inline-flex items-center gap-1"
                style={{ background: secs <= 5 ? "#E3000F" : "#1D4E89" }} data-testid="duel-timer">
                <Clock className="w-4 h-4" /> 00:{String(secs).padStart(2, "0")}
              </motion.div>
            </div>

            {status === "steal" && (
              <div className="brutal-sm p-2 text-black text-center font-display font-black" style={{ background: "#FFD100" }} data-testid="duel-steal-banner">
                🎯 {lang === "es" ? "¡ROBO! Puede contestar el otro equipo" : "STEAL! The other team can answer"}
              </div>
            )}

            <div className="brutal p-4" style={{ background: "#FFF5E1" }}>
              <div className="font-display font-black text-lg leading-tight" data-testid="duel-question">
                {lang === "en" && state.question.prompt_en ? state.question.prompt_en : state.question.prompt_es}
              </div>
            </div>

            {myTurn ? (
              <div className="brutal-sm p-2 text-center font-display font-black text-black inline-flex items-center justify-center gap-1 w-full" style={{ background: "#FFD100" }} data-testid="duel-your-turn">
                <Zap className="w-4 h-4" /> {lang === "es" ? "ES TU TURNO — elige tu respuesta" : "YOUR TURN — pick your answer"}
              </div>
            ) : (
              <div className="text-center text-white/70 text-sm font-body" data-testid="duel-wait-turn">
                {reveal ? (lang === "es" ? "Revelando respuesta…" : "Revealing…")
                  : (lang === "es" ? `Turno de ${state.teams?.[state.active_team]?.players?.find(p => p.seller_id === state.answerer)?.name || state.team_names?.[state.active_team]}…` : "Waiting…")}
              </div>
            )}

            <Options question={state.question} lang={lang} selectable={myTurn} reveal={reveal} onPick={answer} />
          </>
        )}

        {status === "done" && (
          <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="brutal bg-white p-6 text-center space-y-2" data-testid="duel-done">
            <Trophy className="w-12 h-12 text-canales-red mx-auto" />
            <div className="font-display font-black text-3xl">{lang === "es" ? "¡Duelo terminado!" : "Duel over!"}</div>
            {winnerName
              ? <div className="font-display font-black text-2xl" style={{ color: TEAM_COLOR[state.winner_team] }}>🏆 {winnerName}</div>
              : <div className="font-display font-black text-2xl text-canales-red">{lang === "es" ? "¡Empate!" : "Tie!"}</div>}
            <div className="text-sm text-neutral-600">{state.team_names?.A}: {state.team_scores?.A} · {state.team_names?.B}: {state.team_scores?.B}</div>
          </motion.div>
        )}
      </div>
    </DuelShell>
  );
}
